import { useEffect, useLayoutEffect } from "react";
import { COUNTRY_CODE_KEY } from "@/constants/CookieValues";
import {
  COUNTRY_SUPPORTED_LANGUAGE_CODE_MAP,
  LOCALES,
  getDefaultCountryCode,
} from "@/utils/LocaleUtil";

/**
 * **국가 확정 전엔 국가에 따라 바뀌는 표시를 숨긴다** — 첫 화면의 틀린 국가·가격 깜빡임 방지.
 *
 * 첫 화면(SSR·정적 HTML)은 쿠키를 모른다. `useLocale` 의 `countryCode` 는 확정 전 URL 로케일 폴백
 * (`/en` → US)이라, 새로고침·직접 진입·해외 → 해외 국가 변경 직후 국가 표시와 가격이 US 값으로
 * 0.1–0.2초 그려졌다가 바뀌었다(2026-10-12 실측: FR → AL 에서 AL $529 → **US $565** → AL $529).
 * 서버가 쿠키로 그리게 하면 정적 페이지를 요청마다 렌더해야 해서(캐시·속도·범위) 숨김을 택했다.
 *
 * 기전 — **JS 가 안 돌면 숨기지 않는다**:
 * 1. `_document` 의 `<head>` 인라인 스크립트가 `<html data-locale-pending>` 을 붙인다(첫 페인트 전).
 *    JS 가 꺼져 있으면 붙지 않아 아무것도 숨지 않는다.
 *    ★**첫 화면이 이미 확정값인 사용자에겐 붙이지 않는다** — 확정 전 값은 URL 언어의 기본 국가
 *    (`parseLocale(locale).countryCode`), 확정 값은 `getResolvedCountryCode(locale, 쿠키 ?? "US")` 로
 *    쿠키 국가 또는 그 기본 국가다. 그래서 (쿠키 국가 ?? "US") === 기본 국가면 바뀔 수 없다. KR·`/ko/`,
 *    US·`/en`, 쿠키 없음·`/en` 같은 홈마켓이 여기 든다 — 붙이면 이들도 hydration 까지(모바일 CPU x4 실측
 *    0.6초대) 가격·국기가 숨었다(#5988 리뷰 R1). 7 로케일 × 쿠키 82종 = 574조합 대조에서 거짓 음성 0.
 * 2. 전역 CSS 가 그동안 `[data-locale-dependent]` 를 `visibility: hidden` 으로 둔다 — 자리는 유지.
 * 3. 국가가 확정되면(`isCountryHydrated`) `useLocaleReadyAttribute` 가 표시를 뗀다.
 * 4. 앱 JS 가 깨져 3번이 안 오면 인라인 스크립트가 `LOCALE_PENDING_FALLBACK_MS` 뒤 스스로 뗀다.
 *
 * 붙이는 대상은 **쿠키 국가로 값이 바뀌는 표시만** — `GePrice`, GNB 국가 버튼, 「Save $X」 배지.
 * 서버가 확정한 금액(주문 내역·결제 요약)은 국가와 무관해 붙이지 않는다.
 */
export const LOCALE_PENDING_ATTR = "data-locale-pending";
export const LOCALE_DEPENDENT_ATTR = "data-locale-dependent";
export const LOCALE_PENDING_FALLBACK_MS = 3000;

/**
 * **GE flag 를 모르는 첫 순간의 가격 숨김** — `<html data-flag-pending>`.
 *
 * GE 국가 가격은 flag 에 따라 바뀐다 — 유로존·영국은 flag ON 이면 EUR/GBP 고정가(로딩 중엔 USD, #5041),
 * 그 외(Dynamic)는 USD 를 그린 뒤 GE 가 현지 통화로 바꾼다. 그런데 flag 는 클라가 받아 오므로 첫 화면은
 * 모른다. dev 실측(2026-10-12): 프랑스 직접 진입 `$ 529`(0.2초) → `€ 579`, 안도라 → 알바니아 새로고침 뒤
 * `$ 529`(0.1초), 일본 `/ja/` 는 국가 확정 숨김 대상이 아니라 GE 주입 전까지 `$` 가 보였다.
 *
 * 그래서 **마지막으로 받은 flag 값을 기억**(`GE_FLAG_MEMORY_KEY`, `GlobalEScripts` 가 저장)하고, 부트
 * 스크립트가 「지난번 ON + 쿠키 국가가 KR·US 가 아님」이면 첫 페인트 전에 붙인다. KR 은 GE 를 안 쓰고,
 * US 는 `usPrice` 가 flag 와 무관하다. `GlobalEScripts` 가 flag·국가를 확정하면 뗀다(Dynamic 은 그때
 * `data-ge-pending` 으로 이어받는다). 첫 방문(기억 없음)과 flag 가 바뀐 직후 1회는 기억이 틀릴 수 있다 —
 * 틀려도 「숨김만」 이거나 「종전 깜빡임」이고, `FLAG_PENDING_FALLBACK_MS` 뒤 스스로 뗀다.
 * flag OFF 인 prod 는 기억이 「0」이라 붙지 않는다.
 *
 * ★**예외 — 일본 첫 방문(기억 없음)도 붙인다.** 일본은 Dynamic 인데 **우리 엔화 가격표**(`prices.JPY`)가 있는 유일한
 * 국가라, flag 를 모르는 첫 화면이 그 값을 그린다. flag ON 이면 GE 환산가로 바뀌어 **같은 엔화인데 숫자가 바뀐다**
 * (dev 2026-10-13 첫 방문: `¥ 82,900`(보임) → 숨김 → `¥ 86,400`). 다른 국가의 첫 방문은 「$ → 현지 통화」라 종전대로
 * 둔다. 대가: flag OFF 인 prod 에서도 일본 첫 방문 1회는 flag 응답까지(최대 `FLAG_PENDING_FALLBACK_MS`) 가격이
 * 숨는다 — 그 뒤엔 기억이 「0」이라 붙지 않는다.
 *
 * ★두 가지(「지난번 ON」·「일본 첫 방문」) 모두 **앱이 실제로 쓰는 국가**로 판정한다 — `useLocale` 의 `getResolvedCountryCode(주소 언어, 쿠키 ?? "US")`:
 * 쿠키 국가가 주소 언어를 지원하면 그 국가, 아니면 주소 언어의 기본 국가(`/ja` → JP). 그래서 `/ja` + 쿠키 SG · `/ja` + 쿠키
 * 없음도 JP 다. 종전엔 쿠키 국가만 봐서 둘을 놓쳤다(#6007 후속). 같은 국가를 보는지는 테스트가 전 언어 × 전 쿠키로 잠근다.
 *
 * 「쿠키 없는 첫 방문」은 **환경마다 다르다**(#6012 R1):
 *  - Vercel(staging·prod)의 사람 — 미들웨어가 첫 응답에 `bh_ccode` 를 심는다(`needsLocaleCookie` · `x-vercel-ip-country` 필요).
 *    `/ja` 면 IP 국가가 일본어를 지원할 때 그 국가, 아니면 JP → 이 판정은 쿠키로 끝난다
 *  - dev(Cloud Run — IP 헤더 없음)와 봇(`isBotUA`, prod 포함) — 첫 응답에 쿠키가 없다 → 위 규칙의 「쿠키 없음」 분기
 *  ⚠️ dev 에서 잰 첫 방문은 미들웨어 쿠키 경로가 빠진 상태라 prod 대표값이 아니다(dev 2026-10-14 `¥ 82,900` 노출이 그것)
 *
 * flag 조회가 실패해도(`undefined`) `GlobalEScripts` 가 바로 뗀다(그때 화면은 flag OFF 가 최종) — 안 떼면 기억이 안 남아
 * 일본은 **매 방문** 5초가 된다(#6007 R1).
 */
export const FLAG_PENDING_ATTR = "data-flag-pending";
export const FLAG_PENDING_FALLBACK_MS = 5000;
export const GE_FLAG_MEMORY_KEY = "bh_ge_flag";

// 국가 → 지원 언어(표에 없는 국가는 영어만) — 일본 예외가 앱의 `getResolvedCountryCode` 와 같은 국가를 보게 싣는다
const SUPPORTED_LANGUAGES = JSON.stringify(COUNTRY_SUPPORTED_LANGUAGE_CODE_MAP);

// 로케일 → 기본 국가 표 — 빌드 때 `getDefaultCountryCode` 로 만든다(사본을 두지 않는다)
const DEFAULT_COUNTRY_BY_LOCALE = JSON.stringify(
  Object.fromEntries(LOCALES.map((l) => [l, getDefaultCountryCode(l)])),
);

/**
 * `_document` `<head>` 에 그대로 싣는 부트 스크립트 — 첫 페인트 전에 돈다.
 *
 * 쿠키 판정은 `readCountryFromCookie()` 와 같다 — 두 글자 영문만 인정해 대문자화(`parseCountryCode`),
 * 없거나 형식이 틀리면 `"US"`. 같은 이름 쿠키가 여러 스코프에 있으면 **마지막 값** — `cookies-next`(4.3.0)
 * 클라이언트 `getCookies` 가 `document.cookie` 를 앞에서부터 덮어써 마지막 값을 돌려준다(`cookies-next/lib/index.js`
 * 의 `_cookies[name_1] = _cookie`). 첫 값을 읽으면 둘이 다른 국가를 보게 된다(#5988 R3). 로케일은 Next 가 `<html lang>` 에 넣는 값(`ko` · `en` …)이다.
 */
export const localePendingBootScript = `(function(d){function hold(a,ms){d.setAttribute(a,"");setTimeout(function(){d.removeAttribute(a)},ms)}var r=/(?:^|; )${COUNTRY_CODE_KEY}=([^;]*)/g,x,v;while((x=r.exec(document.cookie)))v=x[1];var c=v&&/^[A-Za-z]{2}$/.test(v)?v.toUpperCase():"US",g=${DEFAULT_COUNTRY_BY_LOCALE}[d.lang]||"US",k=(${SUPPORTED_LANGUAGES}[c]||["en"]).indexOf(d.lang)>=0?c:g;if(c!==g)hold("${LOCALE_PENDING_ATTR}",${LOCALE_PENDING_FALLBACK_MS});var f=null;try{f=localStorage.getItem("${GE_FLAG_MEMORY_KEY}")}catch(e){}if((f==="1"&&k!=="KR"&&k!=="US")||(f===null&&k==="JP"))hold("${FLAG_PENDING_ATTR}",${FLAG_PENDING_FALLBACK_MS})})(document.documentElement);`;

/** 국가에 따라 바뀌는 표시에 붙인다 — `{...localeDependentProps}` */
export const localeDependentProps = { [LOCALE_DEPENDENT_ATTR]: "" } as Record<
  `data-${string}`,
  string
>;

// 서버엔 layout effect 가 없다 — 클라에서만 layout(페인트 전)으로 뗀다
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * 국가가 확정된 **그 커밋의 페인트 전에** 숨김을 푼다. 확정 커밋에서 이미 맞는 값이 그려졌으니
 * 처음 보이는 값이 최종값이다(일반 effect 면 확정 전 값이 한 프레임 보일 수 있다).
 */
export function useLocaleReadyAttribute(isCountryHydrated: boolean) {
  useIsomorphicLayoutEffect(() => {
    if (isCountryHydrated) {
      document.documentElement.removeAttribute(LOCALE_PENDING_ATTR);
    }
  }, [isCountryHydrated]);
}
